import { useCallback, useState } from "react";
import { Icon, useToast, CircularLoader, IconName } from "@introist/react-foundation/v2";
import { useDropzone, FileRejection } from "react-dropzone";
import cx from "classnames";

import { uploadAsset } from "services/api/AssetsApi";

import styles from "./ImageUploadArea.module.scss";
import styled from "styled-components";

type ImageUploadAreaProps = {
  imageUrl?: string | null;
  alt?: string;
  icon?: IconName;
  onImageUploaded: (url: string) => unknown;
  maxSize?: number;
  acceptedFileTypes?: string[];
};

const SubduedIcon = styled(Icon)`
  fill: var(--palette-foreground-subdued);
`;

const StyledCircularLoader = styled(CircularLoader)`
  width: 20px;
  height: 20px;
`;

export const ImageUploadArea = ({
  imageUrl,
  alt,
  icon,
  onImageUploaded,
  maxSize = 2 * 1024 * 1024, // 2MB default
  acceptedFileTypes = [".jpeg", ".jpg", ".png", ".svg"]
}: ImageUploadAreaProps) => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);

  const onDrop = useCallback(
    async (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      if (rejectedFiles && rejectedFiles.length > 0) {
        const rejection = rejectedFiles[0];
        const errors = rejection.errors.map(e => e.code);

        if (errors.includes('file-too-large')) {
          toast.error(`File is too large. Maximum size is ${maxSize / 1024 / 1024}MB`);
        } else if (errors.includes('file-invalid-type')) {
          toast.error(`Invalid file type. Accepted: ${acceptedFileTypes.join(', ')}`);
        } else {
          toast.error("Failed to upload image");
        }
        return;
      }

      if (acceptedFiles && acceptedFiles.length > 0) {
        setLoading(true);
        try {
          const acceptedFile = acceptedFiles[0];
          const assetUrl = await uploadAsset(acceptedFile as any);
          onImageUploaded(assetUrl);
        } catch (e) {
          toast.error("Failed to upload image");
        } finally {
          setTimeout(() => setLoading(false), 500);
        }
      }
    },
    [onImageUploaded, toast, maxSize, acceptedFileTypes]
  );

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "image/*": acceptedFileTypes
    },
    onDrop,
    maxSize,
    multiple: false
  });

  return (
    <div {...getRootProps({ className: cx(styles.Root, { [styles.Dropzone]: !imageUrl }) })}>
      <input {...getInputProps()} />
      {!imageUrl && !loading && (
        <SubduedIcon
          name={icon || "logOut"}
          style={{ transform: !icon ? "rotate(90deg)" : undefined }}
        />
      )}
      {imageUrl && !loading && <img src={imageUrl} alt={alt || ""} />}
      {loading && <StyledCircularLoader />}
    </div>
  );
};
